import React, { useState } from 'react';

const SourcesPills = ({ sources }) => {
  const [showAll, setShowAll] = useState(false);
  const maxVisible = 6;
  
  if (!sources || sources.length === 0) return null;

  // Extract URL and title from different source formats
  const normalizedSources = sources.map((source, index) => {
    if (typeof source === 'string') {
      // Handle markdown link format: [title](url)
      const markdownMatch = source.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (markdownMatch) {
        return {
          id: `source-${index}`,
          title: markdownMatch[1],
          url: markdownMatch[2]
        };
      }
      // Handle plain URL
      return {
        id: `source-${index}`,
        title: source,
        url: source
      };
    }
    // Handle object format
    return {
      id: `source-${index}`,
      title: source.title || source.url,
      url: source.url
    };
  });

  const visibleSources = showAll ? normalizedSources : normalizedSources.slice(0, maxVisible);
  const hasMore = normalizedSources.length > maxVisible;

  const getFaviconUrl = (url) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
    } catch (e) {
      return null;
    }
  };

  const getWebsiteName = (url) => {
    try {
      const domain = new URL(url).hostname;
      // Remove www. if present and capitalize first letter
      const cleanDomain = domain.replace(/^www\./, '');
      return cleanDomain.charAt(0).toUpperCase() + cleanDomain.slice(1);
    } catch (e) {
      return 'Unknown Site';
    }
  };

  const formatTitle = (title, maxLength = 35) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength - 3) + '...';
  };

  return (
    <div className="mt-4 space-y-2">
      <div className="text-xs font-medium text-gray-600 dark:text-gray-400">
        Sources:
      </div>
      
      <div className="flex flex-wrap gap-1.5 pb-4">
        {visibleSources.map((source) => (
          <a
            key={source.id}
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-1.5 px-2 py-1.5 bg-gray-100 hover:bg-gray-200 
                     dark:bg-[#111111] dark:hover:bg-[#1A1A1A] rounded-full border border-[#282828] 
                     dark:border-[#282828] transition-colors duration-200 min-h-[36px]"
          >
            {/* Favicon */}
            <img
              src={getFaviconUrl(source.url)}
              alt=""
              className="w-3 h-3 rounded-sm flex-shrink-0 self-center"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            
            {/* Website name and article title */}
            <div className="flex flex-col min-w-0">
              {/* Website name */}
              <span className="text-gray-700 dark:text-gray-300 group-hover:text-gray-900 
                             dark:group-hover:text-gray-100 font-medium text-xs truncate leading-tight whitespace-nowrap">
                {getWebsiteName(source.url)}
              </span>
              {/* Article title */}
              <span className="text-gray-500 dark:text-gray-400 text-[10px] truncate leading-tight whitespace-nowrap">
                {formatTitle(source.title)}
              </span>
            </div>
            
            {/* External link icon */}
            <svg 
              className="w-2.5 h-2.5 text-gray-400 group-hover:text-gray-600 dark:text-gray-500 
                       dark:group-hover:text-gray-300 flex-shrink-0 opacity-70 self-center"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        ))}
        
        {/* Show more/less button */}
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="inline-flex items-center gap-1 py-1.5 text-xs text-gray-600 
                     dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 
                      transition-colors 
                     duration-200 min-h-[36px]"
          >
            {showAll ? (
              <>
                Show less
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </>
            ) : (
              <>
                +{normalizedSources.length - maxVisible} more
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default SourcesPills; 